/**
 * CoPilot Shell
 *
 * Pure sidebar frame — handles fixed positioning, open/close animation,
 * header (avatar, title, close button) and layout spacer.
 * Renders its children as the active mode content (Brain, Guide, Hybrid, etc.).
 */

import { HeroAvatar, X } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { Button, cn } from '@tonnie/ui';
import { type ReactNode, useCallback } from 'react';

import { CoPilotBetaBadge } from './CoPilotBetaBadge';

interface ICoPilotShellProps {
  children: ReactNode;
  /**
   * Open state + closer, injected by the host app. The shell used to read
   * journey-web's sidebar context directly; taking them as props is what makes
   * this frame shareable by BOTH portals (each app owns its own sidebar
   * context with a different value shape).
   */
  isOpen: boolean;
  onClose: () => void;
  /** Header copy — defaults to the citizen Companion strings. */
  title?: string;
  subtitle?: string;
  /** Header avatar — defaults to the Tonnie CoPilot hero avatar. */
  avatar?: ReactNode;
  /**
   * Render the "Beta" badge + hover explainer next to the title. The host app
   * passes its `ENABLE_COPILOT_BETA` flag so the whole beta layer drops cleanly
   * when the flag is turned off. Defaults to off.
   */
  showBeta?: boolean;
}

export function CoPilotShell({
  children,
  isOpen,
  onClose,
  title,
  subtitle,
  avatar,
  showBeta = false,
}: ICoPilotShellProps) {
  const { t } = useTranslation();

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <>
      {/* Spacer — reserves layout space when the sidebar is open */}
      <div
        className={cn(
          'relative w-96 bg-transparent transition-[width] duration-200 ease-linear shrink-0',
          !isOpen && 'w-0'
        )}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <div
        className={cn(
          'flex fixed inset-y-0 right-0 z-40 h-screen w-96 border-l border-copilot-border transition-[transform] duration-200 ease-linear',
          !isOpen && 'translate-x-full'
        )}
        role="dialog"
        aria-label={t('copilot.title')}
        aria-hidden={!isOpen}
        // When collapsed the panel is off-screen (translate-x-full) but its
        // close button + message input stay in the DOM. `aria-hidden` alone
        // leaves them focusable → axe `aria-hidden-focus`. `inert` removes the
        // whole subtree from focus + the a11y tree while closed (React 19).
        inert={!isOpen}
        data-testid="copilot-shell"
      >
        <div className="flex flex-col h-full bg-copilot-surface text-copilot-foreground w-full">
          {/* Header */}
          <div className="border-b border-copilot-border h-16">
            <div className="flex items-center justify-between gap-3 px-6 h-full">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 overflow-hidden bg-copilot-accent/10 border-2 border-copilot-accent/20">
                  {avatar ?? <HeroAvatar hero="copilot" size="xs" className="w-full h-full" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h2 className="text-sm font-semibold text-copilot-foreground leading-tight truncate">
                      {title ?? t('copilot.title')}
                    </h2>
                    {showBeta && <CoPilotBetaBadge />}
                  </div>
                  <p className="text-2xs text-copilot-muted leading-tight">
                    {subtitle ?? t('copilot.brainSubtitle')}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="size-9"
                aria-label={t('copilot.close')}
                data-testid="copilot-shell-close"
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </Button>
            </div>
          </div>

          {children}
        </div>
      </div>
    </>
  );
}
